// Matches C# LabelSchema in AssetLabel.cs

export type RegulatoryInfoType = 'Abv' | 'Recycling' | 'Warning' | 'Other';

export const REGULATORY_INFO_TYPES: readonly RegulatoryInfoType[] = [
  'Abv',
  'Recycling',
  'Warning',
  'Other',
] as const;

export interface DimensionsSchema {
  height?: number;
  width?: number;
  depth?: number;
  diameter?: number;
  unit: string; // default "cm"
}

export interface WeightSchema {
  grossWeight?: number;
  netWeight?: number;
  unit: string; // default "kg"
}

export interface MaterialsSchema {
  visible?: string[];
  inferred?: string[];
}

export interface PackagingSchema {
  count?: number;
  volume?: string;
  materials?: MaterialsSchema;
  recyclingLabels?: string[];
  inBox?: boolean;
}

export interface MarketingCopySchema {
  title?: string;
  content?: string;
  keyFeatures?: string[];
}

export interface RegulatoryInfoSchema {
  type: RegulatoryInfoType;
  value: string;
  additionalDetails?: string;
}

export interface IngredientDetailSchema {
  name: string;
  quantityInfo?: string;
  notes?: string;
}

export interface IngredientsSchema {
  rawText?: string;
  items: IngredientDetailSchema[];
  allergens?: string[];
}

export interface NutrientSchema {
  name: string;
  dailyValue?: string;
  perServingAmount?: number;
  perServingUnit?: string;
  referenceLabel?: string;
  referenceAmount?: number;
  referenceUnit?: string;
  perReferenceAmount?: number;
  perReferenceUnit?: string;
  notes?: string;
}

export interface NutritionFactsSchema {
  servingSize?: string;
  servingsPerContainer?: string;
  nutrients: NutrientSchema[];
  notes?: string;
}

export interface EnvironmentalInfoSchema {
  accreditationMarkings?: string[];
  environmentalClaims?: string[];
  animalWelfareClaims?: string[];
}

export interface UsageInstructionsSchema {
  storage?: string;
  usage?: string;
  assembly?: string;
  care?: string;
  preparation?: string;
}

export interface BrandExtensionSchema {
  subBrand?: string;
  variantDescription?: string;
  shortDescription?: string;
  colourDescription?: string;
  keywords?: string;
  care?: string;
  preparation?: string;
}

export interface LabelSchema {
  brand: string;
  productName: string;
  marketingCopy?: MarketingCopySchema[];
  dimensions?: DimensionsSchema;
  weight?: WeightSchema;
  productCode?: string;
  barcode?: string;
  origin?: string;
  manufacturer?: string;
  website?: string;
  packaging?: PackagingSchema;
  regulatoryInfo: RegulatoryInfoSchema[];
  additionalInfo?: Record<string, string>;
  productMaterials?: MaterialsSchema;
  nutritionFacts: NutritionFactsSchema;
  ingredients: IngredientsSchema;
  environmentalInfo?: EnvironmentalInfoSchema;
  usageInstructions?: UsageInstructionsSchema;
  brandExtension?: BrandExtensionSchema;
  accessibilityDescription: string;
  schemaVersion: number;
  model: string;
}

// All field keys from LabelSchema
export const LABEL_SCHEMA_FIELDS: readonly (keyof LabelSchema)[] = [
  'brand',
  'productName',
  'marketingCopy',
  'dimensions',
  'weight',
  'productCode',
  'barcode',
  'origin',
  'manufacturer',
  'website',
  'packaging',
  'regulatoryInfo',
  'additionalInfo',
  'productMaterials',
  'nutritionFacts',
  'ingredients',
  'environmentalInfo',
  'usageInstructions',
  'brandExtension',
  'accessibilityDescription',
  'schemaVersion',
  'model',
] as const;
